import { useState } from 'react'
import type { ReminderRule } from '../../lib/db'
import { CATEGORIES } from '../record/constants'
import { v4 as uuid } from 'uuid'

type FormRule = Omit<ReminderRule, 'id'>

interface Props {
  initial?: ReminderRule
  onSave: (rule: ReminderRule) => void
  onCancel: () => void
}

function empty(): FormRule {
  return {
    name: '',
    category: null,
    interval_km: null,
    interval_months: null,
    warn_before_km: 300,
    warn_before_days: 14,
    last_done_date: null,
    last_done_km: null,
    enabled: true,
  }
}

export default function ReminderRuleForm({ initial, onSave, onCancel }: Props) {
  const [form, setForm] = useState<FormRule>(() =>
    initial ? { ...initial } : empty()
  )
  const [err, setErr] = useState('')

  function set<K extends keyof FormRule>(key: K, val: FormRule[K]) {
    setForm(f => ({ ...f, [key]: val }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.name.trim()) { setErr('請輸入名稱'); return }
    if (!form.interval_km && !form.interval_months) {
      setErr('請至少設定一種週期（里程或月數）'); return
    }
    setErr('')
    onSave({ ...form, id: initial?.id ?? uuid() })
  }

  return (
    <form className="rule-form" onSubmit={handleSubmit}>
      <h3 className="section-title">{initial ? '編輯' : '新增'}提醒規則</h3>

      <div className="field">
        <label>名稱 *</label>
        <input value={form.name} onChange={e => set('name', e.target.value)}
          placeholder="例：機油、後輪胎" autoComplete="off" />
      </div>

      <div className="field">
        <label>對應類別（選填）</label>
        <select value={form.category ?? ''} onChange={e => set('category', e.target.value || null)}>
          <option value="">不指定</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="rule-intervals">
        <div className="field">
          <label>里程週期（km）</label>
          <input inputMode="numeric" placeholder="例：3000"
            value={form.interval_km ?? ''}
            onChange={e => set('interval_km', e.target.value ? parseInt(e.target.value) : null)} />
        </div>
        <div className="field">
          <label>月數週期</label>
          <input inputMode="numeric" placeholder="例：6"
            value={form.interval_months ?? ''}
            onChange={e => set('interval_months', e.target.value ? parseInt(e.target.value) : null)} />
        </div>
      </div>

      <div className="rule-intervals">
        <div className="field">
          <label>提前警告（km）</label>
          <input inputMode="numeric" value={form.warn_before_km}
            onChange={e => set('warn_before_km', parseInt(e.target.value) || 300)} />
        </div>
        <div className="field">
          <label>提前警告（天）</label>
          <input inputMode="numeric" value={form.warn_before_days}
            onChange={e => set('warn_before_days', parseInt(e.target.value) || 14)} />
        </div>
      </div>

      <div className="rule-intervals">
        <div className="field">
          <label>上次完成日期</label>
          <input type="date" value={form.last_done_date ?? ''}
            onChange={e => set('last_done_date', e.target.value || null)} />
        </div>
        <div className="field">
          <label>上次完成里程</label>
          <input inputMode="numeric" placeholder="km"
            value={form.last_done_km ?? ''}
            onChange={e => set('last_done_km', e.target.value ? parseInt(e.target.value) : null)} />
        </div>
      </div>

      <label className="toggle-row">
        <input type="checkbox" checked={form.enabled}
          onChange={e => set('enabled', e.target.checked)} />
        啟用此提醒
      </label>

      {err && <p className="field-warn">{err}</p>}

      <div className="import-actions">
        <button type="submit" className="submit-btn">儲存</button>
        <button type="button" className="card-btn" onClick={onCancel}>取消</button>
      </div>
    </form>
  )
}
